import React from 'react';
import './index.scss';
import { useNavigate } from 'react-router-dom';
import { atom, useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import { fetchQuestionListState, quizState, submitAmswerState, userSelectedIdsState } from '@/models/quizState';
import { paperState } from '@/pages/Mobile/MPaper';
import { Toast } from 'antd-mobile';
import produce from 'immer';
import { formatSeconds, isMobile } from '@/utils/util';
import { CError } from '@/utils/server';
import { countdownState } from '@/pages/Mobile/MQuiz';
import { LoadingPage } from '../Loading';

interface AlertProps {
    message: string;
    onCancelClick: () => void;
    onConfirmClick: () => void;
}

export enum QuizAlertTypes {
    START = 'start',
    TIMEOUT = 'timeout',
    PAPER = 'paper',
    SORRY = 'sorry',
    REMIND = 'remind',
    HOME = 'Home',
    GUIDE = 'guide',
    HONORS = 'honors',
}

export const alertLoadingState = atom<boolean>({
    key: 'alertLoadingState',
    default: false
})


const Alert: React.FC<AlertProps> = ({ message, onCancelClick, onConfirmClick }) => {

    const quiz = useRecoilValue(quizState)

    const alertLoading = useRecoilValue(alertLoadingState)

    const onlyConfirm = quiz.alertType === QuizAlertTypes.TIMEOUT || quiz.alertType === QuizAlertTypes.SORRY

    const controlStyle = !onlyConfirm ? 'space-between' : 'center'

    return (
        <div className="alert">
            <div className='container'>
                <div className={isMobile() ? 'alert-content' : 'pc-alert-content'}>
                    <p className="message">{message}</p>
                    <div className="control" style={{ justifyContent: controlStyle }}>
                        {
                            !onlyConfirm && (
                                <div className="cancel" onClick={
                                    (event) => {
                                        event.stopPropagation()
                                        onCancelClick()
                                    }
                                }></div>
                            )
                        }
                        <div className={!onlyConfirm ? 'ok' : 'confirm'} onClick={
                            (event) => {
                                event.stopPropagation()
                                onConfirmClick()
                            }
                        }></div>
                    </div>
                </div>
            </div>
            {
                alertLoading && <div className='alertLoading'>
                    <LoadingPage />
                </div>
            }
        </div>
    );
};


export const QuizAlert = () => {

    const navigate = useNavigate()

    const [quiz, setQuiz] = useRecoilState(quizState)

    const [paper, setPaper] = useRecoilState(paperState)

    const [alertLoading, setAlertLoading] = useRecoilState(alertLoadingState)

    const [userSelectedIds, setUserSelectedIds] = useRecoilState(userSelectedIdsState)

    const [countdown, setCountdown] = useRecoilState(countdownState)

    const submitAnswers = useRecoilCallback(({ snapshot }) => async () => {
        try {
            setAlertLoading(true)
            const result = await snapshot.getPromise(submitAmswerState)
            setAlertLoading(false)
            setPaper(result)
            navigate('/paper', { replace: true })
        } catch (error) {
            setAlertLoading(false)
            const err = error as CError
            if(err.code === 756004){
                navigate('/', { replace: true })
            }
            Toast.show({ content: err.message })
        }
    }, [alertLoading, paper])

    const fetchQuestionList = useRecoilCallback(({ snapshot }) => async () => {
        try {
            const qid = quiz.questionnaire?.questionnaire_id ?? ''
            const result = await snapshot.getPromise(fetchQuestionListState({ 'questionnaire_id': qid, '__': `${Math.random()}` }))
            setQuiz(produce(quiz, (draftState) => {
                draftState.questionList = result
                draftState.alert = false
            }))
            setCountdown(0)
            setUserSelectedIds({})
            navigate('/quiz')
        } catch (error) {
            const err = error as CError
            Toast.show({ content: err.message })
        }
    }, [quiz, countdown, userSelectedIds])


    let message = ''
    if (quiz.alertType === QuizAlertTypes.START) {
        message = `参与挑战题库答题可以获得成就奖励\n挑战题库每次限时${formatSeconds(quiz.questionnaire?.answer_time ?? 0)}\n每天可参与${quiz.questionnaire?.times ?? 0}次\n确认要开始挑战答题吗？`
    } else if (quiz.alertType === QuizAlertTypes.TIMEOUT) {
        message = '时间已到，本次答题结束！'
    } else if (quiz.alertType === QuizAlertTypes.PAPER) {
        const count = Object.values(userSelectedIds).reduce((acc, arr) => acc + arr.length, 0)
        if (count < quiz.questionList.length) {
            message = '你还有未作答的题目，确定要提交吗？'
        } else {
            message = '确定要提交吗？'
        }
    } else if (quiz.alertType === QuizAlertTypes.SORRY) {
        message = '对不起\n本日已没有剩余的挑战次数！'
    } else if (quiz.alertType === QuizAlertTypes.REMIND) {
        message = '确定退出本次答题吗？'
    } else if (quiz.alertType === QuizAlertTypes.HOME) {
        message = '确定要返回首页吗？'
    } else if (quiz.alertType === QuizAlertTypes.GUIDE) {
        message = '确定离开答题页面吗？'
        if (quiz.questionnaire && quiz.questionnaire?.times > 0) {
            message += '离开后剩余挑战次数会减少'
        }
    } else if (quiz.alertType === QuizAlertTypes.HONORS) {
        message = '确定离开答题页面吗？'
        if (quiz.questionnaire && quiz.questionnaire?.times > 0) {
            message += '离开后剩余挑战次数会减少'
        }
    }

    return (
        <Alert message={message} onCancelClick={() => {
            setQuiz(produce(quiz, (draftState) => {
                draftState.alert = false
            }))
        }} onConfirmClick={() => {
            setQuiz(produce(quiz, (draftState) => {
                draftState.alert = false
            }))
            if (quiz.alertType === QuizAlertTypes.PAPER) {
                submitAnswers()
            } else if (quiz.alertType === QuizAlertTypes.START) {
                fetchQuestionList()
            } else if (quiz.alertType === QuizAlertTypes.HOME) {
                navigate('/', { replace: true })
            } else if (quiz.alertType === QuizAlertTypes.REMIND) {
                navigate(-1)
            } else if (quiz.alertType === QuizAlertTypes.GUIDE) {
                navigate('/guide', { replace: true })
            } else if (quiz.alertType === QuizAlertTypes.HONORS) {
                navigate(`/honors/${Math.random()}`)
            }
        }} />
    )
}